import { formatCurrency } from '@/app/utils/formatCurrency';
import type { AmountBasis } from '../utils/amountBasis';
import type { SelectionSummaryLine } from '../utils/selectionSummaryLines';

interface Props {
    lines: SelectionSummaryLine[];
    lineMessages?: Map<string, string>;
    basis?: AmountBasis;
}

const messagesFor = (line: SelectionSummaryLine, lineMessages?: Map<string, string>): string[] => {
    const found = line.rowKeys.flatMap((key) => lineMessages?.get(key) ?? []);
    return [...new Set(found)];
};

/** Amounts are the retail prices recorded at sale, with no trade rate, so no
 *  explanatory pricing note is rendered. */
export function InvoiceSelectionLines({ lines, lineMessages, basis = 'net' }: Props) {
    if (lines.length === 0) {
        return <p className="text-sm text-mp-muted">Select a service, item or add-on to invoice.</p>;
    }

    const hasMessages = lines.some((line) => messagesFor(line, lineMessages).length > 0);

    return (
        <div className="space-y-3">
            {hasMessages && (
                <p role="alert" tabIndex={-1} data-rejection-target className="text-sm text-mp-error">
                    Some lines were not accepted. Each is marked below.
                </p>
            )}
            {/* Focusable so a keyboard-only user can reach rows below the cap (WCAG 2.1.1). */}
            <ul
                tabIndex={0}
                aria-label="Selected lines"
                className="max-h-64 space-y-2 overflow-y-auto focus-visible:outline-hidden focus-visible:ring-3 focus-visible:ring-mp-primary"
            >
                {lines.map((line) => (
                    <li key={line.key} className="text-sm">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-mp-foreground">{line.label}</span>
                            <span className="text-mp-foreground">{formatCurrency(line[basis])}</span>
                        </div>
                        {line.serviceName && <p className="text-xs text-mp-muted">{line.serviceName}</p>}
                        {messagesFor(line, lineMessages).map((message) => (
                            <p key={message} className="text-mp-error">{message}</p>
                        ))}
                    </li>
                ))}
            </ul>
        </div>
    );
}
